/**
 * useClientCapabilities — Non-visual state machine for the v1 client
 * capability contract (server: ``GET /api/client/capabilities``).
 *
 * PHASE STATUS: Visual integration is on HOLD pending selected design
 * direction. This module is the reusable non-visual foundation only:
 * auth/adapter lifecycle logic + bounded error kinds + safety
 * predicate. It contains ZERO JSX, ZERO styling, ZERO icons, and
 * makes no assumptions about layout, theming, or copy.
 *
 * DESIGN INVARIANTS (source-grounded; enforced by tests)
 * ------------------------------------------------------
 *
 * 1. The server is the SOLE authority for Founder authorization.
 *    Missing / invalid / expired bearer → 401. Authenticated
 *    non-Founder → 403. The hook does NO client-side Founder probe:
 *    a separate ``/api/gtm/me`` lookup would (a) create a second
 *    async chain to guard, (b) misclassify network / expired-token
 *    errors as "non-Founder", and (c) create a stale-read attack
 *    surface. The 200/401/403/5xx status from the capability
 *    endpoint itself is the truth signal.
 *
 * 2. The bearer is the ONLY thing the adapter transports. The
 *    transport built here reprojects headers to a fresh object
 *    containing at most ``Authorization``; no other client hints
 *    reach the wire.
 *
 * 3. **Composite session-identity ownership** — ownership is keyed
 *    by BOTH ``session.user.id`` AND ``session.access_token``, and
 *    ownership is stored INSIDE the committed state snapshot (not a
 *    mutable ref). Every returned value is masked at render time
 *    when the committed snapshot's ownership tuple does not match
 *    the currently-observed ``(userId, accessToken)`` pair — this
 *    covers three replacement scenarios:
 *      * different user (account switch)
 *      * signed-in → signed-out (signout)
 *      * SAME user, NEW access token (session refresh / rotation)
 *    Ownership living in the SAME state snapshot as
 *    contract/errorKind guarantees masking is synchronous with the
 *    committed values — a torn read cannot expose a previous
 *    identity's contract.
 *
 *    The ``access_token`` used as the discriminator is stored only
 *    in the private internal state slot; it is NEVER returned by
 *    the hook, NEVER logged, NEVER placed in an error message,
 *    NEVER exposed to consumers. Tests assert this negative.
 *
 * 4. **Unmount safety** — the auto-load effect's cleanup increments
 *    the generation counter, so ANY pending async continuation that
 *    was already in flight at unmount time will fail its
 *    ``myGen === genRef.current`` guard and return without writing
 *    state. This closes the "promise resolves after unmount" race.
 *    Combined with ``AbortController.abort()`` this also cancels
 *    the in-flight ``fetch`` where the transport supports it.
 *
 * 5. All error surfaces are bounded generic kinds. No raw exception
 *    text, no server ``detail`` echo, no bearer echo, no interpolated
 *    server-supplied literals — that boundedness is inherited from
 *    the adapter's error contract and preserved here.
 *
 * 6. ``isCapabilityReady`` is a UI PRESENTATION HINT — never an
 *    execution permission. It returns true ONLY for the strict
 *    conjunction ``known_implemented ∧ not_held ∧ verified``. Even
 *    then the server must re-authorize the action at invocation.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth as defaultUseAuth } from "@/auth/AuthProvider";
import { getAccessToken as defaultGetAccessToken } from "@/lib/supabaseClient";
import { fetchClientCapabilities as defaultFetchClientCapabilities } from "@/lib/clientCapabilities";

// ---------------------------------------------------------------------------
// Bounded error kinds — the hook NEVER surfaces raw error text.
// Consumers map these to their own copy/icons at the design boundary.
// ---------------------------------------------------------------------------

/**
 * Adapter error code (bounded) → UI error kind (bounded).
 *
 * Adapter codes (fixed, see clientCapabilities.js):
 *   transport_not_injected | session_not_injected | session_error |
 *   no_bearer | transport_error | transport_no_response |
 *   http_401 | http_403 | http_404 | http_4xx | http_5xx |
 *   http_unexpected_status | malformed_body |
 *   unsupported_contract_version | malformed_body_capabilities_not_array
 *
 * UI kinds (bounded):
 *   signin_required | non_founder | network_error | incompatible |
 *   server_error
 */
export function mapAdapterErrorToKind(errorCode) {
    switch (errorCode) {
        case "http_401":
        case "session_error":
        case "no_bearer":
            return "signin_required";
        case "http_403":
            return "non_founder";
        case "transport_error":
        case "transport_no_response":
            return "network_error";
        case "unsupported_contract_version":
        case "malformed_body":
        case "malformed_body_capabilities_not_array":
            return "incompatible";
        case "http_5xx":
        case "http_unexpected_status":
        case "http_4xx":
        case "http_404":
        case "transport_not_injected":
        case "session_not_injected":
        default:
            return "server_error";
    }
}

/**
 * Strict UI-presentation predicate. NEVER an execution permission.
 * Returns true ONLY when all three axes hold:
 *   implementation_status === "known_implemented"
 *   enablement            === "not_held"
 *   readiness             === "verified"
 *
 * ``declared_enabled`` is NOT part of the predicate — it is a
 * declaration, not proof of implementation or readiness. In v1
 * every fact's ``readiness`` is ``"unverified"`` by design, so
 * this predicate returns ``false`` for every v1 capability by
 * construction. That is intentional.
 */
export function isCapabilityReady(fact) {
    if (!fact || typeof fact !== "object") return false;
    return (
        fact.implementation_status === "known_implemented" &&
        fact.enablement === "not_held" &&
        fact.readiness === "verified"
    );
}

// ---------------------------------------------------------------------------
// Transport / session builders (module-private)
// ---------------------------------------------------------------------------

function _makeTransport({ signal, base, fetchImpl }) {
    return async ({ method, path, headers }) => {
        // Only the Authorization header passes through — even if the
        // adapter or a future variant ever tried to send additional
        // hints, this fence blocks them at the wire.
        const outHeaders = {};
        if (headers && typeof headers.Authorization === "string") {
            outHeaders.Authorization = headers.Authorization;
        }
        const url = `${base || ""}${path}`;
        const resp = await fetchImpl(url, {
            method,
            headers: outHeaders,
            credentials: "omit",
            signal,
        });
        let body = null;
        try {
            body = await resp.json();
        } catch (_e) {
            body = null;
        }
        return { status: resp.status, body };
    };
}

function _makeSession({ getAccessToken }) {
    return {
        getBearerToken: async () => {
            const t = await getAccessToken();
            return t || "";
        },
    };
}

// ---------------------------------------------------------------------------
// Hook — non-visual state machine
// ---------------------------------------------------------------------------

// The initial snapshot represents "no committed state for any identity"
// — ownerId and ownerToken are both null so the ownership mask fires
// against any non-null (userId, accessToken) until the first fetch
// lands.
const _INITIAL_SNAPSHOT = Object.freeze({
    state: "loading",
    errorKind: null,
    contract: null,
    ownerId: null,
    ownerToken: null,
});

const _SIGNED_OUT_SNAPSHOT = Object.freeze({
    state: "signed_out",
    errorKind: null,
    contract: null,
    ownerId: null,
    ownerToken: null,
});

/**
 * State literals returned:
 *   "auth_loading" — auth provider is still resolving
 *   "signed_out"   — no active session
 *   "loading"      — fetch in flight (post-mount or after refresh())
 *   "ok"           — contract loaded successfully
 *   "error"        — with ``errorKind`` populated from the bounded set
 *
 * Options:
 *   deps: {
 *     useAuth, getAccessToken, fetchClientCapabilities,
 *     backendBase, fetchImpl
 *   }
 *   All keys are optional and default to the production wiring.
 *   Tests inject their own to exercise the state machine without
 *   touching network / Supabase / global fetch.
 */
export function useClientCapabilities({ deps = {} } = {}) {
    const {
        useAuth: useAuthImpl = defaultUseAuth,
        getAccessToken: getAccessTokenImpl = defaultGetAccessToken,
        fetchClientCapabilities: fetchAdapter = defaultFetchClientCapabilities,
        backendBase = (typeof process !== "undefined" && process.env
            ? process.env.REACT_APP_BACKEND_URL
            : undefined),
        fetchImpl = (typeof fetch !== "undefined" ? fetch : undefined),
    } = deps;

    const { session, loading: authLoading } = useAuthImpl();
    const userId = session?.user?.id ?? null;
    // Session-identity discriminator: distinguishes same-user session
    // replacement (token rotation) from steady state. Stored ONLY in
    // the private internal snapshot slot below; NEVER returned to the
    // caller, NEVER logged, NEVER placed in an error message.
    const accessToken = session?.access_token ?? null;

    // Composite snapshot — ownership lives INSIDE the committed React
    // state, so masking is synchronous with the committed values. A
    // torn read cannot expose a previous identity's contract because
    // the ownership tuple travels with contract/errorKind atomically.
    const [snapshot, setSnapshot] = useState(_INITIAL_SNAPSHOT);

    // Generation counter — every refresh increments; only writes
    // whose ``myGen === genRef.current`` at resolution time are
    // allowed. Guards:
    //   * stale response after signout (identity-change bumps gen)
    //   * stale response after session replacement (identity-change bumps gen)
    //   * out-of-order retry resolution (each refresh bumps gen)
    //   * unmount races (unmount cleanup bumps gen)
    const genRef = useRef(0);
    const abortRef = useRef(null);

    // Reset on ANY identity change — userId OR access-token. Session
    // replacement for the SAME user is a distinct identity and
    // invalidates any in-flight or committed state.
    useEffect(() => {
        genRef.current += 1;
        if (abortRef.current) {
            try {
                abortRef.current.abort();
            } catch (_e) {
                /* noop */
            }
            abortRef.current = null;
        }
        setSnapshot(_INITIAL_SNAPSHOT);
    }, [userId, accessToken]);

    const refresh = useCallback(async () => {
        if (authLoading) return;
        if (!userId) {
            genRef.current += 1;
            setSnapshot(_SIGNED_OUT_SNAPSHOT);
            return;
        }

        const myGen = ++genRef.current;
        const myUserId = userId;
        // Capture the session-identity discriminator observed at the
        // start of THIS fetch attempt. If session replacement happens
        // mid-flight, the resolved snapshot's ownerToken will not
        // match the current accessToken and the mask will fire.
        const myToken = accessToken;

        if (abortRef.current) {
            try {
                abortRef.current.abort();
            } catch (_e) {
                /* noop */
            }
        }
        const controller = new AbortController();
        abortRef.current = controller;

        // Reset to a masked loading snapshot (ownerId/ownerToken both
        // null → ownership will not match ANY signed-in identity until
        // this fetch lands).
        setSnapshot(_INITIAL_SNAPSHOT);

        // Fetch the capability contract via the PR#148 adapter. The
        // adapter's bounded error codes are the SOLE authoritative
        // source of error classification.
        const transport = _makeTransport({
            signal: controller.signal,
            base: backendBase,
            fetchImpl,
        });
        const sessionAdapter = _makeSession({ getAccessToken: getAccessTokenImpl });
        let result;
        try {
            result = await fetchAdapter({ transport, session: sessionAdapter });
        } catch (_e) {
            // Adapter should never throw — a rejected transport is
            // caught internally. Defensive-only. No raw exception
            // text propagates through this path.
            if (myGen !== genRef.current) return;
            setSnapshot({
                state: "error",
                errorKind: "network_error",
                contract: null,
                ownerId: myUserId,
                ownerToken: myToken,
            });
            return;
        }
        if (myGen !== genRef.current) return; // stale — drop
        if (!result || result.ok !== true) {
            setSnapshot({
                state: "error",
                errorKind: mapAdapterErrorToKind(result?.error || "server_error"),
                contract: null,
                ownerId: myUserId,
                ownerToken: myToken,
            });
            return;
        }
        setSnapshot({
            state: "ok",
            errorKind: null,
            contract: result.contract,
            ownerId: myUserId,
            ownerToken: myToken,
        });
    }, [authLoading, userId, accessToken, backendBase, fetchImpl, getAccessTokenImpl, fetchAdapter]);

    // Auto-load on mount + any identity change (via refresh's deps).
    useEffect(() => {
        refresh();
        return () => {
            // UNMOUNT-SAFETY INVARIANT: bump the generation so any
            // promise still in flight at unmount time will fail its
            // next ``myGen === genRef.current`` check and return
            // WITHOUT writing state. AbortController.abort()
            // additionally cancels the in-flight fetch where the
            // transport supports it.
            genRef.current += 1;
            if (abortRef.current) {
                try {
                    abortRef.current.abort();
                } catch (_e) {
                    /* noop */
                }
            }
        };
    }, [refresh]);

    // Synchronous ownership mask — the committed snapshot's ownership
    // tuple must match the CURRENT (userId, accessToken) pair. Any
    // mismatch (account switch / signout / session replacement /
    // pre-first-fetch) fully masks the returned payload.
    const ownershipMatches =
        snapshot.ownerId === userId && snapshot.ownerToken === accessToken;
    const shouldMask = authLoading || !userId || !ownershipMatches;

    const returnedState = authLoading
        ? "auth_loading"
        : !userId
            ? "signed_out"
            : ownershipMatches
                ? snapshot.state
                : "loading";

    const returnedContract = shouldMask ? null : snapshot.contract;
    const returnedErrorKind = shouldMask ? null : snapshot.errorKind;

    // isFounder is DERIVED (never a separate state slot). Strict
    // booleans only — no truthy coercion.
    //   state === "ok"                → true  (server accepted; 200)
    //   errorKind === "non_founder"   → false (server 403; authoritatively denied)
    //   any other state               → null
    let returnedIsFounder;
    if (shouldMask) {
        returnedIsFounder = null;
    } else if (snapshot.state === "ok") {
        returnedIsFounder = true;
    } else if (snapshot.errorKind === "non_founder") {
        returnedIsFounder = false;
    } else {
        returnedIsFounder = null;
    }

    // Return surface — ONLY these fields. ownerToken is NEVER exposed.
    return {
        state: returnedState,
        errorKind: returnedErrorKind,
        contract: returnedContract,
        isFounder: returnedIsFounder,
        refresh,
    };
}

// Named-export the private builders ONLY for tests that need to
// audit their behaviour (transport header projection). Not part of
// the stable public surface.
export const __private__ = {
    makeTransport: _makeTransport,
    makeSession: _makeSession,
};
